import { Icon, Select, SvgIconNames } from '@toggle/design-system';
import React, { RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '~/components/filter/Option';
import { FilterOption } from '~/components/update-filter-options/updateFilterOptions';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { WebSearch } from '~/views/alfred/research/services/conversations-service';
import { useChat } from '~/views/alfred/research/use-chat/useChat';

import { TrustedSourcesModal } from './TrustedSourcesModal';
import * as S from './WebSearchDropdown.styles';

interface DropdownOption {
  key: string;
  name: string;
  icon: SvgIconNames;
  description: string;
  value: {
    web_search: WebSearch;
  };
  isChecked: boolean;
}

export type WebSearchDropdownVariant = 'icon' | 'select';

export interface WebSearchDropdownProps {
  contentRef?: RefObject<HTMLDivElement | null>;
  variant?: WebSearchDropdownVariant;
}

export const WebSearchDropdown = ({
  contentRef,
  variant = 'icon',
}: WebSearchDropdownProps) => {
  const { t } = useTranslation(['chat']);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { webSearch, setWebSearch } = useChat(state => ({
    webSearch: state.webSearch,
    setWebSearch: state.setWebSearch,
  }));
  const { trackAlfredWebSearchSelected } = usePosthogEvents();

  const options: DropdownOption[] = [
    {
      key: 'open',
      name: t('chat:trustedSources.general.title'),
      icon: 'Globe',
      description: t('chat:trustedSources.general.description'),
      value: {
        web_search: 'open',
      },
      isChecked: webSearch === 'open',
    },
    {
      key: 'restricted',
      name: t('chat:trustedSources.restricted.title'),
      icon: 'GlobeLocked',
      description: t('chat:trustedSources.restricted.description'),
      value: {
        web_search: 'restricted',
      },
      isChecked: webSearch === 'restricted',
    },
    {
      key: 'block',
      name: t('chat:trustedSources.block.title'),
      icon: 'GlobeOff',
      description: t('chat:trustedSources.block.description'),
      value: {
        web_search: 'block',
      },
      isChecked: webSearch === 'block',
    },
  ];

  const selectedOption = options.find(
    option => option.value.web_search === webSearch
  );

  const renderCustomFilterOptionLabel = (filterOption: FilterOption) => {
    return (
      <S.OptionWrapper>
        {filterOption.icon && <Icon iconName={filterOption.icon} size={16} />}
        <S.Label>{filterOption.name}</S.Label>
      </S.OptionWrapper>
    );
  };

  const renderTrigger = () => {
    if (variant === 'select') {
      return (
        <S.SelectWrapper>
          <Select
            isActive={dropdownOpen}
            inputProps={{
              value: selectedOption?.name ?? '',
              variant: 'outline',
              'data-testid': 'web-search-dropdown-trigger',
              leftIcon: selectedOption?.icon ? (
                <Icon iconName={selectedOption.icon} size={16} />
              ) : undefined,
            }}
          />
        </S.SelectWrapper>
      );
    }

    return (
      <div>
        <S.StyledButton
          variant="empty"
          iconName={selectedOption?.icon}
          size="small"
          data-testid="web-search-dropdown-trigger"
        />
      </div>
    );
  };

  const dropdownMenu = (
    <S.StyledDropdownMenu
      contentRef={contentRef}
      triggerNode={renderTrigger()}
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      $variant={variant}
      contentProps={
        variant === 'select'
          ? {
              style: {
                width: 'var(--radix-dropdown-menu-trigger-width)',
                minWidth: 'var(--radix-dropdown-menu-trigger-width)',
                maxWidth: 'var(--radix-dropdown-menu-trigger-width)',
              },
            }
          : undefined
      }
    >
        {options.map(option => {
          return (
            <div key={option.key}>
              <Option
                option={option}
                isChecked={option.isChecked}
                indentedDescription={24}
                handleClick={() => {
                  setWebSearch(option.value.web_search);
                  const typeMap: Record<WebSearch, 'general' | 'trusted' | 'none'> = {
                    open: 'general',
                    restricted: 'trusted',
                    block: 'none',
                  };
                  trackAlfredWebSearchSelected(
                    POSTHOG_TRACKING_SOURCES.ALFRED,
                    typeMap[option.value.web_search]
                  );
                }}
                renderCustomFilterOptionLabel={renderCustomFilterOptionLabel}
              />
              {option.key === 'restricted' && (
                <S.StyledXsButton
                  variant="tertiary"
                  size="xsmall"
                  label={t('chat:trustedSources.viewTrustedSources')}
                  onClick={() => {
                    setDropdownOpen(false);
                    setModalOpen(true);
                  }}
                />
              )}
            </div>
          );
        })}
    </S.StyledDropdownMenu>
  );

  return (
    <>
      {dropdownMenu}
      {modalOpen && (
        <TrustedSourcesModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};
